import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { ReactComponent as Close } from "../../assets/icons/x.svg";
import { Check, Loader } from "react-feather";
import ForgotPasswordForm from "./ForgotPasswordForm";
import VerificationCode from "./VerificationCode";
import ResetPassword from "./ResetPassword";
import PasswordChanged from "./PasswordChanged";
import { getQueryParam, useScreenTransition } from "../../Hooks/useScreenTransition";
import { AuthContext } from "../../Context/AuthContext";
import { NotificationContext } from "../../Context/NotificationContext";
import "./SignInForm.css";
import { FormTextInput } from "../../Components/FormTextInput";
import { auth, firestore } from "../../firebase.config";
import { useLocation } from "react-router-dom";

export default function SignInForm({
  open,
  isLoginFormOpen,
  isCreateAccountOpen,
  pageRedirect = true,
  showDirectForgotPassword = false
}) {
  const modalRoot = document.getElementById("modal-root");

  const { signedIn, setSignedIn } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [signInState, setSignInState] = useState("login");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const { transitionTo} = useScreenTransition();

  const joinTeamParam= getQueryParam("join-team");
  const joinTeamToken= getQueryParam("token");

  const isLoginOpen = () => {
    isLoginFormOpen(false);
    setSignInState("login");
    setEmailError(false);
    setPasswordError(false);
    setShowPassError(false);
  };

  const isCreateAccount = () => {
    isLoginFormOpen(false);
    isCreateAccountOpen(true);
  };

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [btnText, setBtnText] = useState("Sign in");
  const [showPassError, setShowPassError] = useState(false);

  const { showNotification } = useContext(NotificationContext);


  useEffect(() => {
    if (signedIn) {
      isLoginOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  useEffect(()=> {
    if(showDirectForgotPassword){
      handleForgotPassword();
    }
  }, [showDirectForgotPassword])

  const handleEmailChange = (value) => {
    setEmailError(false);
    setEmail(value);
  };

  const handlePasswordChange = (value) => {
    setPasswordError(false);
    setPassword(value);
  };

  const handleForgotPassword = () => {
    setSignInState("forgot-password");
  };

  const handleVerification = () => {
    setSignInState("verification");
  };

  const handleResetPassword = () => {
    setSignInState("reset-password");
  };

  const handlePasswordChanged = () => {
    setSignInState("password-changed");
  };

  const backToSignIn = () => {
    setSignInState("login");
  };

  const signIn = () => {
    if (email === "" || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
    }

    if (password === "" || password.length < 6) {
      setPasswordError(true);
    }

    if (
      email === "" ||
      !/\S+@\S+\.\S+/.test(email) ||
      password === "" ||
      password.length < 6
    ) {
      return;
    }


    setBtnText("Signing");
    setIsLoading(true);
    auth.signInWithEmailAndPassword(email.toLowerCase(), password)
      .then(async (credential) => {
        if(joinTeamParam && joinTeamToken){
            await firestore.collection("users").doc(credential.user.uid).update({
              hasTeam: true,
              teamId: joinTeamToken || "",
              teamJoinTimestamp: new Date().getTime(),
              teamRole: "member",
              teamStatus: "active",
            });
        }
        if (pageRedirect) {
          joinTeamParam && joinTeamToken ? transitionTo("/account/team-members") : transitionTo("/account/profile");
        }
        setBtnText("Sign in");
        setSignedIn(true);
        setIsLoading(false);
      })
      .catch((e) => {
        // console.log(e);
        setBtnText("Sign in");
        setIsLoading(false);
        showNotification(
          "Error",
          "Invalid Email or Password, Either the email is incorrect or the password. Please check and try again.",
          "error"
        );
      });
  };

  const triggerSignIn = () => {
    if (!(email === "" || password === "")) {
      signIn();
    }
  };

  if (!open) {
    return <></>;
  }

  const handleEmailBlur = () => {
    if (email === "" || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
    }
  };

  const handlePasswordBlur = () => {
    if (password === "" || password.length < 6) {
      setPasswordError(true);
    }
    setShowPassError(true);
  };
  return ReactDOM.createPortal(
    <div className="modal">
      <div className="modal-content">
        {signInState === "login" ? (
          <div className="signInForm">
            <div className="signInFormHeader">
              <h2 className="signInFormTitle">Sign In</h2>
              <Close className="signInFormIcon" onClick={isLoginOpen} />
            </div>

            <div className="signInFormContainer">
              <FormTextInput
                email={true}
                incorrect={emailError}
                placeholder={"Email"}
                onBlur={handleEmailBlur}
                clearInputIcon={true}
                value={email}
                onChange={handleEmailChange}
                onKeyDown={(e) => e.key === "Enter" && triggerSignIn()}
              />
            </div>

            {emailError ? (
              <span className="signInFormInputValidation">
                Invalid email format. Example: example@example.com
              </span>
            ) : null}

            <div className="signInFormContainer">
              <FormTextInput
                showPasswordIcon={true}
                password={true}
                incorrect={passwordError}
                onBlur={handlePasswordBlur}
                placeholder={"Password"}
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={(e) => e.key === "Enter" && triggerSignIn()}
              />
            </div>

            {showPassError && (
              <span
                className={`passwordValidation ${
                  passwordError
                    ? "passwordError"
                    : password.length >= 6
                    ? "passwordSuccess"
                    : "passwordDefault"
                }`}
              >
                <Check className="passwordCheck" />
                At least 6 characters
              </span>
            )}

            <span
              className="signInFormForgotPassword"
              onClick={handleForgotPassword}
            >
              Forgot Password?
            </span>
            <button
              className="signInFormButton"
              disabled={
                email === "" ||
                password === "" ||
                isLoading ||
                emailError ||
                passwordError
              }
              onClick={() => signIn()}
            >
              {btnText}
              {isLoading && <Loader className="btnLoader" />}
            </button>

            <div className="signInFormDontHaveAccount">
              <span className="signInFormDontHaveAccountSpan">
                Don't have an account?
              </span>
              <span
                className="signInFormDontHaveAccountRegister"
                onClick={() => isCreateAccount()}
              >
                Register
              </span>
            </div>
          </div>
        ) : null}
        {signInState === "forgot-password" ? (
          <ForgotPasswordForm
            isLoginOpen={isLoginOpen}
            goToVerification={handleVerification}
            backToSignIn={backToSignIn}
            email={forgotPasswordEmail}
            setEmail={setForgotPasswordEmail}
          />
        ) : null}

        {signInState === "verification" ? (
          <VerificationCode
            backToSignIn={backToSignIn}
            isLoginOpen={isLoginOpen}
            goToResetPassword={handleResetPassword}
            email={forgotPasswordEmail}
          />
        ) : null}

        {signInState === "reset-password" ? (
          <ResetPassword
            isLoginOpen={isLoginOpen}
            backToSignIn={backToSignIn}
            handlePasswordChanged={handlePasswordChanged}
            email={forgotPasswordEmail}
          />
        ) : null}

        {signInState === "password-changed" ? (
          <PasswordChanged
            backToSignIn={backToSignIn}
            isLoginOpen={isLoginOpen}
          />
        ) : null}
      </div>
    </div>,
    modalRoot
  );
}
